
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Check, Link2 } from 'lucide-react';
import { EcommercePlatform, EcommerceSettings } from '@/components/admin/campaigns/types';

interface EcommerceConnectionTabProps {
  platform: EcommercePlatform;
  settings: EcommerceSettings;
  updateSettings: (settings: Partial<EcommerceSettings>) => void;
}

const EcommerceConnectionTab: React.FC<EcommerceConnectionTabProps> = ({
  platform,
  settings,
  updateSettings,
}) => {
  const [testConnectionStatus, setTestConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const getPlatformName = (platform: EcommercePlatform): string => {
    const platformMap: Record<EcommercePlatform, string> = {
      shopify: 'Shopify',
      woocommerce: 'WooCommerce',
      bigcommerce: 'BigCommerce',
      volusion: 'Volusion',
      wordpress: 'WordPress',
      squarespace: 'Squarespace',
      magento: 'Magento',
      shift4shop: 'Shift4Shop'
    };
    
    return platformMap[platform] || platform;
  };
  
  const handleTestConnection = () => {
    if (!settings.storeUrl) {
      setErrorMessage("Store URL is required");
      setTestConnectionStatus('error');
      return;
    }
    
    if ((platform === 'shopify' || platform === 'bigcommerce' || platform === 'magento') 
        && (!settings.apiKey || !settings.apiSecret)) {
      setErrorMessage("API Key and API Secret are required");
      setTestConnectionStatus('error');
      return;
    }
    
    // Simulate API connection test
    setTestConnectionStatus('testing');
    setTimeout(() => {
      setTestConnectionStatus('success');
      updateSettings({ isConnected: true });
    }, 1500);
  };
  
  const getApiKeyLabel = () => {
    switch (platform) {
      case 'shopify':
        return 'API Key';
      case 'woocommerce':
        return 'Consumer Key';
      case 'bigcommerce':
        return 'Client ID';
      case 'magento':
        return 'Access Token';
      default:
        return 'API Key';
    }
  };
  
  const getApiSecretLabel = () => {
    switch (platform) {
      case 'shopify':
        return 'API Secret Key';
      case 'woocommerce':
        return 'Consumer Secret';
      case 'bigcommerce':
        return 'Client Secret';
      case 'magento':
        return 'Access Token Secret';
      default:
        return 'API Secret';
    }
  };
  
  const showApiFields = () => {
    // Some platforms may not require API keys
    return platform !== 'wordpress' && platform !== 'squarespace';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Connect to {getPlatformName(platform)}</CardTitle>
        <CardDescription>
          Set up the connection to your {getPlatformName(platform)} store to enable referral campaigns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="storeUrl">Store URL</Label>
          <Input
            id="storeUrl"
            placeholder={`https://your-store.${platform === 'shopify' ? 'myshopify.com' : 'com'}`}
            value={settings.storeUrl}
            onChange={(e) => updateSettings({ storeUrl: e.target.value })}
          />
          <p className="text-sm text-muted-foreground">
            Enter the URL of your {getPlatformName(platform)} store
          </p>
        </div>
        
        {showApiFields() && (
          <>
            <div className="space-y-2">
              <Label htmlFor="apiKey">{getApiKeyLabel()}</Label>
              <Input
                id="apiKey"
                type="password"
                placeholder="Enter your API key"
                value={settings.apiKey}
                onChange={(e) => updateSettings({ apiKey: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="apiSecret">{getApiSecretLabel()}</Label>
              <Input
                id="apiSecret"
                type="password"
                placeholder="Enter your API secret"
                value={settings.apiSecret}
                onChange={(e) => updateSettings({ apiSecret: e.target.value })}
              />
            </div>
          </>
        )}
        
        {platform === 'shopify' && (
          <Alert className="bg-muted">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Shopify API Access</AlertTitle>
            <AlertDescription>
              You need to create a custom app in your Shopify Admin to get API credentials.
              Go to Apps &gt; Develop apps &gt; Create an app.
            </AlertDescription>
          </Alert>
        )}
        
        {platform === 'woocommerce' && (
          <Alert className="bg-muted">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>WooCommerce REST API</AlertTitle>
            <AlertDescription>
              You need to create API keys in your WooCommerce admin.
              Go to WooCommerce &gt; Settings &gt; Advanced &gt; REST API &gt; Add key.
            </AlertDescription>
          </Alert>
        )}
        
        {testConnectionStatus === 'error' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Connection Failed</AlertTitle>
            <AlertDescription>
              {errorMessage || `Failed to connect to your ${getPlatformName(platform)} store. Please check your credentials.`}
            </AlertDescription>
          </Alert>
        )}
        
        {testConnectionStatus === 'success' && (
          <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
            <Check className="h-4 w-4 text-green-600" />
            <AlertTitle>Connection Successful</AlertTitle>
            <AlertDescription>
              Successfully connected to your {getPlatformName(platform)} store.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleTestConnection}
          disabled={testConnectionStatus === 'testing'}
          className="w-full"
        >
          {testConnectionStatus === 'testing' ? 'Testing Connection...' : (
            <>
              <Link2 className="mr-2 h-4 w-4" />
              Test Connection
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default EcommerceConnectionTab;
